import { LOCALE_COOKIE } from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import type { StatsigLocaleDynamicConfig } from '@src/config/localisation/statsig-locale-config.types'
import { getLocaleConfigFromMemory } from '@src/modules/global-state/locale-config.browser'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { IZipCodeInfo } from '@src/server-side/shared-server-side.types'
import { getCookieStorage } from '@src/utils/enhancedCookie'
import { getCountryPrefixedCookieKey } from '@src/utils/i18n.utils'

import isBrowser from '../../../utils/isBrowser'
import { USER_ENTERED_ZIP_COOKIE_KEY, ZIP_COOKIE_KEY, IP_ADDRESS_COOKIE_KEY } from '../context/zipcode.context.provider'

/**
 * Validates zipcode using config only. No country-based logic – validation is entirely driven by
 * locale config (from useStatsigDynamicLocaleConfig or getLocaleConfig(locale)). Locale is only
 * used to look up config; the code never branches on country.
 *
 * @param zipcode - Zipcode to validate
 * @param localeConfig - Optional full locale config (zipcode.regex etc.); if missing, resolved from locale cookie
 * @returns true if zipcode matches config regex
 */
export function validateZipCode(zipcode?: string | null, localeConfig?: StatsigLocaleDynamicConfig): boolean {
  if (!zipcode) return false

  let config = localeConfig
  if (!config) {
    config = getLocaleConfigFromMemory()
  }

  const regexPattern = config?.zipcode?.regex
  if (!regexPattern) return false
  try {
    // eslint-disable-next-line i18next/no-literal-string
    return new RegExp(regexPattern).test(zipcode)
  } catch (e) {
    console.warn('Invalid zipcode regex in locale config', { regexPattern, error: e })
    return false
  }
}

/**
 * Validates zipcode against config for a locale. No country branching – locale is only used to look up
 * config (getLocaleConfig); validation uses config.zipcode.regex only.
 *
 * @param zipcode - The zipcode/postal code to validate
 * @param locale - Used only to resolve config when localeConfig is not provided
 * @param localeConfig - Optional; if provided, used directly (no locale lookup)
 * @returns true if zipcode matches the config regex
 */
export function isValidZipCodeForLocale(
  zipcode: string | undefined,
  locale?: string,
  localeConfig?: StatsigLocaleDynamicConfig,
): boolean {
  if (!zipcode) return false

  let config = localeConfig
  if (!config) {
    config = getLocaleConfigFromMemory()
  }

  const regexPattern = config?.zipcode?.regex
  if (!regexPattern) return false
  try {
    // eslint-disable-next-line i18next/no-literal-string
    return new RegExp(regexPattern).test(zipcode)
  } catch (e) {
    console.warn('Invalid zipcode regex in locale config', { regexPattern, error: e })
    return false
  }
}

export function getZipCodeFromCookie(onlyUserEntered = false, localeConfig?: StatsigLocaleDynamicConfig) {
  const locale = getCookie(LOCALE_COOKIE) as TAbsoluteLocale | undefined
  const cookieStorage = getCookieStorage()
  const countryUserZipKey = getCountryPrefixedCookieKey(USER_ENTERED_ZIP_COOKIE_KEY, locale)
  const countryZipKey = getCountryPrefixedCookieKey(ZIP_COOKIE_KEY, locale)
  // Use isGlobal: true because keys are already country-prefixed (us:zip-code),
  // useglobal false will return the key as is, not country-prefixed

  // Helper function to migrate non-prefixed cookie to country-prefixed
  const migrateCookie = (key: string, countryKey: string, value: string) => {
    if (isBrowser()) {
      cookieStorage.setCookieLocalized(countryKey, value, { isGlobal: true })
      cookieStorage.removeCookieLocalized(key, { isGlobal: true })
    }
  }

  // FIRST: Check country-prefixed user-entered zipcode (userEnteredZipCode = user manually entered)
  const countryUserZip = cookieStorage.getCookieLocalized(countryUserZipKey, { isGlobal: true })
  if (countryUserZip && isValidZipCodeForLocale(countryUserZip, locale, localeConfig)) {
    return countryUserZip
  }

  // SECOND: Check non-prefixed user-entered zipcode (fallback)
  const userZip = cookieStorage.getCookieLocalized(USER_ENTERED_ZIP_COOKIE_KEY, { isGlobal: true })
  if (userZip && isValidZipCodeForLocale(userZip, locale, localeConfig)) {
    // Found non-prefixed userEnteredZipCode cookie that is valid - migrate it
    migrateCookie(USER_ENTERED_ZIP_COOKIE_KEY, countryUserZipKey, userZip)

    // Also check and migrate zipCode (from API) if it exists and is valid - only if checking both cookies
    if (!onlyUserEntered) {
      const nonPrefixedZip = cookieStorage.getCookieLocalized(ZIP_COOKIE_KEY, { isGlobal: true })
      if (nonPrefixedZip && isValidZipCodeForLocale(nonPrefixedZip, locale, localeConfig)) {
        migrateCookie(ZIP_COOKIE_KEY, countryZipKey, nonPrefixedZip)
      }
    }
    return userZip
  }

  // If only checking user-entered zip codes, skip API zip code checks
  if (!onlyUserEntered) {
    // THIRD: Check country-prefixed zipcode (zipCode = from API)
    const countryZip = cookieStorage.getCookieLocalized(countryZipKey, { isGlobal: true })
    if (countryZip && isValidZipCodeForLocale(countryZip, locale, localeConfig)) {
      return countryZip
    }

    // FOURTH: Check non-prefixed zipcode (fallback)
    const zip = cookieStorage.getCookieLocalized(ZIP_COOKIE_KEY, { isGlobal: true })
    if (zip && isValidZipCodeForLocale(zip, locale, localeConfig)) {
      // Found non-prefixed zipCode cookie that is valid - migrate it
      migrateCookie(ZIP_COOKIE_KEY, countryZipKey, zip)

      // Also check and migrate userEnteredZipCode if it exists and is valid
      const nonPrefixedUserZip = cookieStorage.getCookieLocalized(USER_ENTERED_ZIP_COOKIE_KEY, { isGlobal: true })
      if (nonPrefixedUserZip && isValidZipCodeForLocale(nonPrefixedUserZip, locale, localeConfig)) {
        migrateCookie(USER_ENTERED_ZIP_COOKIE_KEY, countryUserZipKey, nonPrefixedUserZip)
      }
      return zip
    }
  }

  return null
}

export function getZipCodeModalInitialState() {
  if (isBrowser()) {
    const hash = window.location.hash
    return hash === '#get-zip-code'
  }
  return false
}

export const setZipCodeCookies = (zipCodeInfo: IZipCodeInfo | null, localeConfig?: StatsigLocaleDynamicConfig) => {
  if (!zipCodeInfo) {
    return
  }
  const locale = getCookie(LOCALE_COOKIE) as TAbsoluteLocale | undefined
  const cookieStorage = getCookieStorage()

  if (zipCodeInfo.ipAddress) {
    cookieStorage.setCookieLocalized(IP_ADDRESS_COOKIE_KEY, zipCodeInfo.ipAddress, {
      isGlobal: true,
      expires: undefined, // Session cookie (no expires)
    })
  }
  if (zipCodeInfo.zipCode) {
    const countryKey = getCountryPrefixedCookieKey(ZIP_COOKIE_KEY, locale)
    cookieStorage.setCookieLocalized(countryKey, zipCodeInfo.zipCode, {
      isGlobal: true,
      expires: undefined, // Session cookie (no expires)
    })
    // Only remove non-prefixed cookie if it exists and is valid for current locale
    // This prevents removing cookies from other countries when switching locales
    if (isBrowser()) {
      const nonPrefixedZip = cookieStorage.getCookieLocalized(ZIP_COOKIE_KEY, { isGlobal: true })
      if (nonPrefixedZip && isValidZipCodeForLocale(nonPrefixedZip, locale, localeConfig)) {
        cookieStorage.removeCookieLocalized(ZIP_COOKIE_KEY, { isGlobal: true })
      }
    }
  }
}
